import { MagnifyingGlassIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { AiChatSessionSummary } from './chatHistoryTypes';
import { formatSessionDate, sessionDisplayTitle } from './chatHistoryTypes';

type ChatSessionSidebarProps = {
  sessions: AiChatSessionSummary[];
  activeSessionId: string | null;
  loading: boolean;
  deletingSessionId: string | null;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSelect: (sessionId: string) => void;
  onNew: () => void;
  onDelete: (sessionId: string) => void;
};

export const ChatSessionSidebar = ({
  sessions,
  activeSessionId,
  loading,
  deletingSessionId,
  searchQuery,
  onSearchChange,
  onSelect,
  onNew,
  onDelete,
}: ChatSessionSidebarProps) => {
  return (
    <div className='flex w-36 shrink-0 flex-col border-r border-border bg-muted/30 sm:w-48'>
      <div className='space-y-2 border-b border-border p-2'>
        <button
          type='button'
          onClick={onNew}
          className='flex w-full items-center justify-center gap-1 rounded-md bg-primary px-2 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90'
        >
          <PlusIcon className='h-4 w-4' />
          Nueva
        </button>
        <div className='relative'>
          <MagnifyingGlassIcon className='pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
          <input
            type='search'
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder='Buscar…'
            maxLength={128}
            className='w-full rounded-md border border-input bg-background py-1.5 pl-7 pr-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring'
            aria-label='Buscar conversaciones'
          />
        </div>
      </div>
      <div className='flex-1 overflow-y-auto p-1'>
        {loading && (
          <p className='px-2 py-3 text-xs text-muted-foreground'>Cargando…</p>
        )}
        {!loading && sessions.length === 0 && (
          <p className='px-2 py-3 text-xs text-muted-foreground'>
            {searchQuery.trim() ? 'Sin resultados' : 'Sin conversaciones'}
          </p>
        )}
        {sessions.map((session) => {
          const isActive = session.id === activeSessionId;
          return (
            <div
              key={session.id}
              className={`group mb-1 flex items-start gap-0.5 rounded-md ${
                isActive ? 'bg-accent' : 'hover:bg-accent/60'
              }`}
            >
              <button
                type='button'
                onClick={() => onSelect(session.id)}
                className='min-w-0 flex-1 px-2 py-2 text-left'
                title={sessionDisplayTitle(session)}
              >
                <p className='truncate text-xs font-medium text-foreground'>
                  {sessionDisplayTitle(session)}
                </p>
                <p className='text-[10px] text-muted-foreground'>
                  {formatSessionDate(session.updatedAt)}
                </p>
              </button>
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(session.id);
                }}
                disabled={deletingSessionId === session.id}
                className='mt-1 rounded p-1 text-muted-foreground opacity-0 hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100 disabled:opacity-50'
                aria-label='Eliminar conversación'
              >
                <TrashIcon className='h-3.5 w-3.5' />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
